import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  orderBy,
  where,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './config';
import { Employee, EmployeeFormData, DateAvailability, AccountStatus, EmployeePreferences } from '../../types/employee.types';
import { UserRole } from '../../types/organization.types';
import { authService } from './auth.service';

/**
 * Clean undefined values from an object (Firestore doesn't support undefined)
 * Recursively removes undefined values from nested objects
 */
function cleanUndefinedValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const cleaned: Record<string, unknown> = {};

  for (const key in obj) {
    const value = obj[key];

    if (value === undefined) {
      // Skip undefined values
      continue;
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively clean nested objects
      cleaned[key] = cleanUndefinedValues(value as Record<string, unknown>);
    } else {
      // Keep defined values as-is
      cleaned[key] = value;
    }
  }

  return cleaned as Partial<T>;
}

/**
 * Firebase Employees Service
 * Handles employee CRUD operations for organizations
 */
export const employeesService = {
  /**
   * Create a new employee for an organization
   */
  async createEmployee(organizationId: string, data: EmployeeFormData): Promise<string> {
    try {
      const employeeRef = doc(collection(db, 'organizations', organizationId, 'employees'));
      const employeeId = employeeRef.id;

      const employeeData: Record<string, unknown> = {
        name: data.name,
        email: data.email || '',
        phone: data.phone || '',
        assignedShopIds: data.assignedShopIds || [],
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(employeeRef, employeeData);

      return employeeId;
    } catch (error) {
      console.error('Create employee error:', error);
      throw error;
    }
  },

  /**
   * Get an employee by ID
   */
  async getEmployee(organizationId: string, employeeId: string): Promise<Employee | null> {
    try {
      const employeeDoc = await getDoc(
        doc(db, 'organizations', organizationId, 'employees', employeeId)
      );

      if (employeeDoc.exists()) {
        return {
          id: employeeDoc.id,
          ...employeeDoc.data(),
        } as Employee;
      }

      return null;
    } catch (error) {
      console.error('Get employee error:', error);
      throw error;
    }
  },

  /**
   * Get all employees for an organization
   */
  async getEmployees(organizationId: string): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'organizations', organizationId, 'employees');
      const q = query(employeesRef, orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      const employees: Employee[] = [];
      querySnapshot.forEach((doc) => {
        employees.push({
          id: doc.id,
          ...doc.data(),
        } as Employee);
      });

      return employees;
    } catch (error) {
      console.error('Get employees error:', error);
      throw error;
    }
  },

  /**
   * Get employees by shop assignment
   */
  async getEmployeesByShop(organizationId: string, shopId: string): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'organizations', organizationId, 'employees');
      const q = query(employeesRef, where('assignedShopId', '==', shopId), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      const employees: Employee[] = [];
      querySnapshot.forEach((doc) => {
        employees.push({
          id: doc.id,
          ...doc.data(),
        } as Employee);
      });

      return employees;
    } catch (error) {
      console.error('Get employees by shop error:', error);
      throw error;
    }
  },

  /**
   * Get employees available for any shop (assignedShopId is null)
   */
  async getEmployeesAvailableForAnyShop(organizationId: string): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'organizations', organizationId, 'employees');
      const q = query(employeesRef, where('assignedShopId', '==', null), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);

      const employees: Employee[] = [];
      querySnapshot.forEach((doc) => {
        employees.push({
          id: doc.id,
          ...doc.data(),
        } as Employee);
      });

      return employees;
    } catch (error) {
      console.error('Get employees available for any shop error:', error);
      throw error;
    }
  },

  /**
   * Update an employee
   */
  async updateEmployee(
    organizationId: string,
    employeeId: string,
    data: Partial<EmployeeFormData>
  ): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);

      // Clean undefined values from the update data (Firestore doesn't support undefined)
      const cleanedData = cleanUndefinedValues(data as unknown as Record<string, unknown>);

      await updateDoc(employeeRef, {
        ...cleanedData,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update employee error:', error);
      throw error;
    }
  },

  /**
   * Delete an employee
   * Also cleans up linked user account if one exists
   */
  async deleteEmployee(organizationId: string, employeeId: string): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);

      // Get employee to check for linked user
      const employeeDoc = await getDoc(employeeRef);
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();

        // If employee has a linked user account, remove them from the organization
        if (employeeData.linkedUserId) {
          await authService.removeUserFromOrganization(employeeData.linkedUserId);
        }
      }

      await deleteDoc(employeeRef);
    } catch (error) {
      console.error('Delete employee error:', error);
      throw error;
    }
  },

  /**
   * Update employee status (active/inactive)
   */
  async updateEmployeeStatus(
    organizationId: string,
    employeeId: string,
    status: 'active' | 'inactive'
  ): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);
      await updateDoc(employeeRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update employee status error:', error);
      throw error;
    }
  },

  /**
   * Update availability for a specific date
   */
  async updateDateAvailability(
    organizationId: string,
    employeeId: string,
    date: string,
    availability: DateAvailability
  ): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);

      // Clean undefined values from availability
      const cleanedAvailability = cleanUndefinedValues(availability as unknown as Record<string, unknown>);

      await updateDoc(employeeRef, {
        [`availability.${date}`]: cleanedAvailability,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update date availability error:', error);
      throw error;
    }
  },

  /**
   * Remove availability for a specific date
   */
  async removeDateAvailability(
    organizationId: string,
    employeeId: string,
    date: string
  ): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);

      // To remove a field in Firestore, we need to get the document, modify it, and update it
      const employeeDoc = await getDoc(employeeRef);
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        const availability = { ...(employeeData.availability || {}) };
        delete availability[date];

        await updateDoc(employeeRef, {
          availability,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Remove date availability error:', error);
      throw error;
    }
  },

  /**
   * Get availability for a specific employee and date
   */
  getDateAvailability(employee: Employee, date: string): DateAvailability | null {
    if (!employee.availability || !employee.availability[date]) {
      return null;
    }
    return employee.availability[date];
  },

  /**
   * Bulk update availability for multiple dates in a single batch write
   * More efficient than individual updates when changing multiple days
   */
  async updateMultipleDateAvailability(
    organizationId: string,
    employeeId: string,
    updates: Array<{ date: string; availability: DateAvailability | null }>
  ): Promise<void> {
    if (updates.length === 0) return;

    try {
      const batch = writeBatch(db);
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);

      // Build the update object with all date changes
      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };

      for (const { date, availability } of updates) {
        if (availability === null) {
          // Mark for deletion
          updateData[`availability.${date}`] = deleteField();
        } else {
          // Clean undefined values and add to update
          const cleanedAvailability = cleanUndefinedValues(availability as unknown as Record<string, unknown>);
          updateData[`availability.${date}`] = cleanedAvailability;
        }
      }

      batch.update(employeeRef, updateData);
      await batch.commit();
    } catch (error) {
      console.error('Bulk update date availability error:', error);
      throw error;
    }
  },

  /**
   * Update employee preferences
   */
  async updatePreferences(
    organizationId: string,
    employeeId: string,
    preferences: EmployeePreferences
  ): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);

      // Clean undefined values from preferences
      const cleanedPreferences = cleanUndefinedValues(preferences as unknown as Record<string, unknown>);

      await updateDoc(employeeRef, {
        preferences: cleanedPreferences,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update employee preferences error:', error);
      throw error;
    }
  },

  // ==========================================
  // Account Linking Methods
  // ==========================================

  /**
   * Link an employee record to a user account
   * Includes retry mechanism to handle Firestore propagation delays
   */
  async linkEmployeeToUser(
    organizationId: string,
    employeeId: string,
    userId: string
  ): Promise<void> {
    const maxRetries = 3;
    const retryDelay = 500; // ms

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Small delay before first attempt to allow user document to propagate
        if (attempt === 0) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);
        await updateDoc(employeeRef, {
          linkedUserId: userId,
          accountStatus: 'active' as AccountStatus,
          updatedAt: serverTimestamp(),
        });
        return; // Success, exit the function
      } catch (error) {
        console.error(`Link employee to user error (attempt ${attempt + 1}/${maxRetries}):`, error);

        // If this is the last attempt, throw the error
        if (attempt === maxRetries - 1) {
          throw error;
        }

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  },

  /**
   * Get employee by linked user ID
   */
  async getEmployeeByUserId(
    organizationId: string,
    userId: string
  ): Promise<Employee | null> {
    try {
      const employeesRef = collection(db, 'organizations', organizationId, 'employees');
      const q = query(employeesRef, where('linkedUserId', '==', userId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const docSnap = querySnapshot.docs[0];
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as Employee;
    } catch (error) {
      console.error('Get employee by user ID error:', error);
      throw error;
    }
  },

  /**
   * Update employee account status
   */
  async updateAccountStatus(
    organizationId: string,
    employeeId: string,
    accountStatus: AccountStatus
  ): Promise<void> {
    try {
      const employeeRef = doc(db, 'organizations', organizationId, 'employees', employeeId);
      await updateDoc(employeeRef, {
        accountStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Update account status error:', error);
      throw error;
    }
  },

  /**
   * Get employees without linked accounts
   */
  async getEmployeesWithoutAccounts(organizationId: string): Promise<Employee[]> {
    try {
      const employeesRef = collection(db, 'organizations', organizationId, 'employees');
      // Get all employees and filter client-side (Firestore doesn't support != queries well)
      const querySnapshot = await getDocs(employeesRef);

      const employees: Employee[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Include employees without linkedUserId or with accountStatus !== 'active'
        if (!data.linkedUserId || data.accountStatus !== 'active') {
          employees.push({
            id: doc.id,
            ...data,
          } as Employee);
        }
      });

      return employees;
    } catch (error) {
      console.error('Get employees without accounts error:', error);
      throw error;
    }
  },

  // ==========================================
  // Promotion/Demotion Methods
  // ==========================================

  /**
   * Promote an employee to a staff role (manager/admin)
   * The employee keeps their employee record but gains management access
   */
  async promoteEmployee(
    organizationId: string,
    employeeId: string,
    userId: string,
    newRole: Exclude<UserRole, 'owner' | 'employee'>
  ): Promise<void> {
    try {
      // Update user's role in users collection
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: serverTimestamp(),
      });

      // Add/update user in organization's members collection
      const memberRef = doc(db, 'organizations', organizationId, 'members', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      await setDoc(memberRef, {
        email: userData?.email || '',
        displayName: userData?.displayName || '',
        role: newRole,
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        promotedFromEmployee: true, // Track that this member was promoted from employee
        employeeId, // Keep reference to employee record
      }, { merge: true });
    } catch (error) {
      console.error('Promote employee error:', error);
      throw error;
    }
  },

  /**
   * Demote a promoted employee back to employee-only role
   */
  async demoteEmployee(
    organizationId: string,
    _employeeId: string, // Kept for API consistency, employee record unchanged
    userId: string
  ): Promise<void> {
    try {
      // Update user's role back to employee
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: 'employee' as UserRole,
        updatedAt: serverTimestamp(),
      });

      // Remove from organization's members collection
      const memberRef = doc(db, 'organizations', organizationId, 'members', userId);
      await deleteDoc(memberRef);
    } catch (error) {
      console.error('Demote employee error:', error);
      throw error;
    }
  },

  /**
   * Check if an employee is promoted (has staff role)
   */
  async isEmployeePromoted(
    organizationId: string,
    userId: string
  ): Promise<{ isPromoted: boolean; role?: UserRole }> {
    try {
      const memberRef = doc(db, 'organizations', organizationId, 'members', userId);
      const memberDoc = await getDoc(memberRef);

      if (memberDoc.exists()) {
        const data = memberDoc.data();
        if (data.promotedFromEmployee) {
          return { isPromoted: true, role: data.role as UserRole };
        }
      }

      return { isPromoted: false };
    } catch (error) {
      console.error('Check if employee promoted error:', error);
      throw error;
    }
  },
};
